import React from 'react';
import { Box, ThemeProvider } from '@primer/react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Introduction from './components/Introduction';
import Timeline from './components/Timeline';
import Projects from './components/Projects';
import DSAGuide from './components/DSAGuide';
import DSAFlashcard from './components/DSAFlashcard';
import LearnDSA from './components/LearnDSA';
import Changelog from './components/Changelog';
import Footer from './components/Footer';
import SiteMap from './components/SiteMap';
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"

function AppContent() {
    const location = useLocation();
    
    // Initialize with all filter types selected
    const [timelineFilter, setTimelineFilter] = React.useState(['work', 'internship', 'education']);

    // Determine active section from current route
    const getActiveSection = () => {
        const path = location.pathname;
        if (path === '/timeline') return 'timeline';
        if (path === '/projects') return 'projects';
        if (path === '/learn-dsa') return 'learn-dsa';
        if (path === '/dsa') return 'dsa';
        if (path === '/flashcards') return 'flashcards';
        if (path === '/changelog') return 'changelog';
        return 'intro';
    };

    // Function to handle timeline filter change
    const handleTimelineFilterChange = (filters) => {
        setTimelineFilter(filters);
    };

    // Generate breadcrumb structured data based on current route
    const getBreadcrumbStructuredData = () => {
        const path = location.pathname;
        let breadcrumbs = [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.dylanca.dev/"
            }
        ];

        if (path === '/timeline') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Experience Timeline",
                "item": "https://www.dylanca.dev/timeline"
            });
        } else if (path === '/projects') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Projects",
                "item": "https://www.dylanca.dev/projects"
            });
        } else if (path === '/learn-dsa') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Learn DSA",
                "item": "https://www.dylanca.dev/learn-dsa"
            });
        } else if (path === '/dsa') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "DSA Cheatsheet",
                "item": "https://www.dylanca.dev/dsa"
            });
        } else if (path === '/flashcards') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "DSA Flashcards",
                "item": "https://www.dylanca.dev/flashcards"
            });
        } else if (path === '/changelog') {
            breadcrumbs.push({
                "@type": "ListItem",
                "position": 2,
                "name": "Website Changelog",
                "item": "https://www.dylanca.dev/changelog"
            });
        }

        return {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs
        };
    };

    // Update document head with structured data and page-specific meta tags
    React.useEffect(() => {
        const path = location.pathname;
        
        // Update page title and meta description based on route
        const pageInfo = {
            '/': {
                title: 'Dylan Cattelan | Software Engineer & Backend Developer',
                description: 'Dylan Cattelan - Software Engineer with 5+ years of remote experience. Based in Montreal, specializing in backend development and system design.',
                image: 'https://www.dylanca.dev/og-image.png'
            },
            '/timeline': {
                title: 'Experience Timeline | Dylan Cattelan',
                description: 'Professional experience timeline of Dylan Cattelan - Software Engineer with 6+ years working remotely in France, London, San Francisco, and Montreal.',
                image: 'https://www.dylanca.dev/og-timeline.png'
            },
            '/projects': {
                title: 'Projects | Dylan Cattelan - Software Engineer',
                description: 'Software engineering projects by Dylan Cattelan, featuring backend systems, web applications, and technical implementations.',
                image: 'https://www.dylanca.dev/og-projects.png'
            },
            '/learn-dsa': {
                title: 'Learn DSA | Interactive Data Structures & Algorithms Learning',
                description: 'Interactive platform for learning Data Structures and Algorithms with comprehensive tools, flashcards, and cheatsheets for coding interview preparation.',
                image: 'https://www.dylanca.dev/og-learn-dsa.png'
            },
            '/dsa': {
                title: 'DSA Cheatsheet | Data Structures & Algorithms Guide',
                description: 'Comprehensive Data Structures and Algorithms cheatsheet with examples, explanations, and coding interview preparation resources.',
                image: 'https://www.dylanca.dev/og-dsa.png'
            },
            '/flashcards': {
                title: 'DSA Flashcards | Interactive Learning Tool',
                description: 'Interactive flashcards for learning Data Structures and Algorithms concepts, perfect for coding interview preparation and skill development.',
                image: 'https://www.dylanca.dev/og-flashcards.png'
            },
            '/changelog': {
                title: 'Website Changelog | Dylan Cattelan',
                description: 'Track the evolution of dylanca.dev from a simple portfolio to a comprehensive learning platform. See all features, improvements, and algorithm additions.',
                image: 'https://www.dylanca.dev/og-changelog.png'
            }
        };

        const currentPageInfo = pageInfo[path] || pageInfo['/'];
        
        // Update title
        document.title = currentPageInfo.title;
        
        // Update meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', currentPageInfo.description);
        }
        
        // Update Open Graph title and description
        let ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
            ogTitle.setAttribute('content', currentPageInfo.title);
        }
        
        let ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
            ogDescription.setAttribute('content', currentPageInfo.description);
        }
        
        // Update Twitter title and description
        let twitterTitle = document.querySelector('meta[property="twitter:title"]');
        if (twitterTitle) {
            twitterTitle.setAttribute('content', currentPageInfo.title);
        }
        
        let twitterDescription = document.querySelector('meta[property="twitter:description"]');
        if (twitterDescription) {
            twitterDescription.setAttribute('content', currentPageInfo.description);
        }

        // Update breadcrumb structured data
        const existingScript = document.getElementById('breadcrumb-structured-data');
        if (existingScript) {
            existingScript.remove();
        }

        const script = document.createElement('script');
        script.id = 'breadcrumb-structured-data';
        script.type = 'application/ld+json';
        script.text = JSON.stringify(getBreadcrumbStructuredData());
        document.head.appendChild(script);

        return () => {
            const scriptToRemove = document.getElementById('breadcrumb-structured-data');
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, [location.pathname]);

    return (
        <Box
            bg="canvas.default"
            minHeight="100vh"
            maxWidth="1200px"
            mx="auto"
            px={[1, 3, 4]} // Much smaller padding on mobile
            py={4}
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
            }}
        >
            <Header activeSection={getActiveSection()} />
            <SiteMap />

            <Box as="main">
                <Routes>
                    <Route path="/" element={<Introduction />} />
                    <Route 
                        path="/timeline" 
                        element={
                            <Timeline
                                filter={timelineFilter}
                                onFilterChange={handleTimelineFilterChange}
                            />
                        } 
                    />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/learn-dsa" element={<LearnDSA />} />
                    <Route path="/dsa" element={<DSAGuide />} />
                    <Route path="/flashcards" element={<DSAFlashcard />} />
                    <Route path="/changelog" element={<Changelog />} />
                    {/* Catch-all route: redirect any wrong URLs to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </Box>

            <Footer />
            <Analytics />
            <SpeedInsights />
        </Box>
    );
}

function App() {
    return (
        <ThemeProvider>
            <Router>
                <AppContent />
            </Router>
        </ThemeProvider>
    );
}

export default App;